import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import TagManager from 'react-gtm-module';
import PropTypes from 'prop-types';

import { IMG } from '../../../const';
import { formSubmit } from '../../../redux/slices/formSlice';
import validationSchema from './validationSchema';
import './confirmation.scss';

const ModalBody = ({ onClose, setSending }) => {
  const dispatch = useDispatch();
  const { loading, data } = useSelector((s) => s.form);
  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(validationSchema) });

  const watchedName = watch('name');
  const watchedEmail = watch('email');
  const emptyFields = !watchedName || !watchedEmail;

  const onSubmit = (submitData) => {
    const newData = { ...data, name: submitData.name, email: submitData.email };

    TagManager.dataLayer({
      dataLayer: {
        event: 'form_submit_data',
        formData: newData,
        estimation_result: `https://qa.laestimator.ai/report/${data.page_id}`,
      },
    });

    dispatch(formSubmit(newData));
    setSending(true);
    onClose();
  };

  return (
    <div className="confirmationModal">
      <div className="confirmationModal__body">
        {loading ? (
          <div className="confirmationModal__pending-state">
            <img
              src={`${IMG.QUICK_QUOTE}/email-pending.svg`}
              width="116"
              height="116"
              alt="Sending email"
            />
            <h3>DON'T CLOSE THE WINDOW!</h3>
            <p>
              AI is working, it usually takes 3-5 minutes to generate a result.
            </p>
          </div>
        ) : (
          <>
            <h3>Enter your email address to&nbsp;save the report</h3>
            <p>
              <strong>
                We do not send spam or share information with third parties.
              </strong>
            </p>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <div>
                <input
                  type="text"
                  {...register('name')}
                  placeholder="Name"
                  maxLength={100}
                />
                {errors.name && (
                  <p className="errorMessage">{errors.name.message}</p>
                )}
              </div>
              <div>
                <input
                  type="email"
                  {...register('email')}
                  placeholder="Email"
                />
                {errors.email && (
                  <p className="errorMessage">{errors.email.message}</p>
                )}
              </div>

              {/* {error && <p className="errorMessage">❌ {error}</p>} */}

              <button
                type="submit"
                className="primaryFatLink"
                disabled={emptyFields}
                id="submit-email"
              >
                Submit
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const EmailSubmit = ({ isOpen, onClose, setSending }) => {
  const modalContainerRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (modalContainerRef.current && modalContainerRef.current === e.target) {
        isOpen && onClose();
      }
    };

    window.addEventListener('click', onClickOutside);
    return () => {
      window.removeEventListener('click', onClickOutside);
    };
  });

  return (
    <dialog
      className={`confirmation${isOpen ? ' open' : ''}`}
      ref={modalContainerRef}
    >
      {isOpen ? <ModalBody onClose={onClose} setSending={setSending} /> : null}
    </dialog>
  );
};

export default EmailSubmit;

EmailSubmit.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  setSending: PropTypes.func,
};

ModalBody.propTypes = {
  onClose: PropTypes.func,
  setSending: PropTypes.func,
  loading: PropTypes.bool,
  data: PropTypes.object,
};
